import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Paper,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Movie as MovieIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { createAd, updateAd, getAdminAdById } from "../api/adsApi";
import { ROLES, hasRole } from "../utils/auth";
import { getApiErrorMessage } from "../utils/apiError";

// Available web app pages in Chesswyze
export const AVAILABLE_PAGES = [
  { value: "all", label: "All Pages (Global)" },
  { value: "home", label: "Home / Landing" },
  { value: "dashboard", label: "Dashboard" },
  { value: "chess", label: "Play / Chess Match" },
  { value: "tournaments", label: "Tournaments Listing" },
  { value: "tournament_details", label: "Tournament Details" },
  { value: "leaderboard", label: "Leaderboard" },
  { value: "trainer_profile", label: "Trainer Profile" },
  { value: "lessons", label: "Video Lessons" },
  { value: "subscription", label: "Subscription" },
  { value: "blog", label: "Blogs" },
  { value: "legal", label: "Policies / Legal" },
];

// Available slots according to specs:
// top_banner , footer_banner , join_vedio , left_banner , right_banner
export const AVAILABLE_SLOTS = [
  {
    value: "top_banner",
    label: "Top Banner (Header)",
    badge: "Header Slot",
    desc: "Top banner displayed in header section of selected pages",
  },
  {
    value: "footer_banner",
    label: "Footer Banner",
    badge: "Footer Slot",
    desc: "Banner displayed in footer section of selected pages",
  },
  {
    value: "join_vedio",
    label: "Tournament Join Video",
    badge: "Tournament Join Modal",
    desc: "Video ad shown when a user clicks Join Tournament before confirming join",
  },
  {
    value: "left_banner",
    label: "Left Side Banner",
    badge: "Left Sidebar",
    desc: "Sidebar ad space on left side of eligible views",
  },
  {
    value: "right_banner",
    label: "Right Side Banner",
    badge: "Right Sidebar",
    desc: "Sidebar ad space on right side of eligible views",
  },
];

const LEGACY_SLOT_MAP = {
  global_top_banner: "top_banner",
  tournament_details_banner: "top_banner",
  global_footer_logos: "footer_banner",
  footer_logos: "footer_banner",
  join_video: "join_vedio",
  tournament_list_video: "join_vedio",
  sidebar_left: "left_banner",
  sidebar_right: "right_banner",
};

export const ALL_SPECIFIC_PAGES = AVAILABLE_PAGES.filter((p) => p.value !== "all").map((p) => p.value);

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const MAX_VIDEO_SIZE_BYTES = 200 * 1024 * 1024; // 200 MB

const isImageFileType = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp|svg|bmp|avif)$/i.test(file.name || "");
};

const isVideoFileType = (file) => {
  if (!file) return false;
  if (file.type && file.type.startsWith("video/")) return true;
  return /\.(mp4|webm|ogg|mov|m4v|avi|mkv)$/i.test(file.name || "");
};

const validateMediaFileSize = (file, expectedMediaType) => {
  if (!file) return null;
  const isVideo = expectedMediaType
    ? expectedMediaType === "video"
    : (file.type.startsWith("video/") || /\.(mp4|webm|ogg|mov)$/i.test(file.name));
  const sizeMB = (file.size / (1024 * 1024)).toFixed(2);

  if (isVideo && file.size > MAX_VIDEO_SIZE_BYTES) {
    return `Video file size exceeds maximum allowed 200 MB (selected: ${sizeMB} MB). Please choose a smaller video.`;
  }
  if (!isVideo && file.size > MAX_IMAGE_SIZE_BYTES) {
    return `Image file size exceeds maximum allowed 5 MB (selected: ${sizeMB} MB). Please choose a smaller image.`;
  }
  return null;
};

const checkIsVideo = (url, file, selectedMediaType) => {
  if (file) return isVideoFileType(file);
  if (typeof url === "string" && url) {
    const isVidUrl =
      /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url) ||
      url.includes("/video/") ||
      url.startsWith("data:video/");
    const isImgUrl =
      /\.(jpe?g|png|gif|webp|svg|bmp|avif)($|\?)/i.test(url) ||
      url.includes("/image/") ||
      url.startsWith("data:image/");
    if (isVidUrl) return true;
    if (isImgUrl) return false;
  }
  return selectedMediaType === "video";
};

const checkIsMatchingType = (file, url, expectedType) => {
  if (file) {
    return expectedType === "video" ? isVideoFileType(file) : isImageFileType(file);
  }
  if (url) {
    const isVid = checkIsVideo(url, null, null);
    return expectedType === "video" ? isVid : !isVid;
  }
  return false;
};

const getPagesForPayload = (pages = []) => {
  if (!Array.isArray(pages) || pages.includes("all")) {
    return ALL_SPECIFIC_PAGES;
  }
  return pages;
};

const normalizePagesForUI = (pages = []) => {
  if (!Array.isArray(pages)) return ["all"];
  if (pages.includes("all")) return ["all"];
  if (pages.length >= ALL_SPECIFIC_PAGES.length && ALL_SPECIFIC_PAGES.every((p) => pages.includes(p))) {
    return ["all"];
  }
  return pages.length ? pages : ["all"];
};

const normalizeSpaces = (str) => (str || "").trim().replace(/\s+/g, " ");

// URL validation regex: requires http:// or https:// with valid host/domain/IP and optional port/path/query/fragment
const URL_REGEX = /^https?:\/\/(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}|localhost|(?:\d{1,3}\.){3}\d{1,3})(?::\d{1,5})?(?:[/?#]\S*)?$/i;

const isValidDestinationUrl = (url) => {
  if (!url || !url.trim()) return true;
  const trimmed = url.trim();
  if (!URL_REGEX.test(trimmed)) return false;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
};

const areArraysEqual = (a = [], b = []) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((val, idx) => val === sortedB[idx]);
};

const CreateAd = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const fileInputRef = useRef(null);
  // const mobileFileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [initialData, setInitialData] = useState(null);

  const [formData, setFormData] = useState({
    title: "",
    mediaType: "image", // "image" | "video" | "logo"
    pages: ["all"],
    slots: ["top_banner"],
    linkUrl: "",
    altText: "",
  });

  // Media files & previews
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [existingMediaUrl, setExistingMediaUrl] = useState("");
  const [originalMediaUrl, setOriginalMediaUrl] = useState("");
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!hasRole(ROLES.SUPERADMIN)) {
      navigate("/unauthorized", { replace: true });
    }
  }, [navigate]);

  // Load existing ad if in Edit Mode
  useEffect(() => {
    if (!isEditMode) return;

    const loadAd = async () => {
      try {
        setFetchLoading(true);
        const res = await getAdminAdById(id);
        const ad = res?.data || res;

        let parsedPages = ["all"];
        if (Array.isArray(ad.pages)) {
          parsedPages = ad.pages;
        } else if (typeof ad.pages === "string") {
          try {
            parsedPages = JSON.parse(ad.pages);
          } catch {
            parsedPages = [ad.pages];
          }
        }

        let parsedSlots = ["top_banner"];
        if (Array.isArray(ad.slots)) {
          parsedSlots = ad.slots;
        } else if (typeof ad.slots === "string") {
          try {
            parsedSlots = JSON.parse(ad.slots);
          } catch {
            parsedSlots = [ad.slots];
          }
        }

        // Map any legacy slot keys to the new standard slot keys
        parsedSlots = parsedSlots.map((s) => LEGACY_SLOT_MAP[s] || s);
        const validValues = AVAILABLE_SLOTS.map((s) => s.value);
        parsedSlots = Array.from(new Set(parsedSlots.filter((s) => validValues.includes(s))));
        if (!parsedSlots.length) {
          parsedSlots = ["top_banner"];
        }

        const rawPages = parsedPages.length ? parsedPages : ["all"];
        const loadedPages = normalizePagesForUI(rawPages);
        const loadedSlots = parsedSlots;
        const loadedTitle = ad.title || "";
        const rawMediaType = ad.mediaType || "image";
        const loadedMediaType = rawMediaType === "logo" ? "image" : rawMediaType;
        const loadedLinkUrl = ad.linkUrl || "";
        const loadedAltText = ad.altText || "";

        setFormData({
          title: loadedTitle,
          mediaType: loadedMediaType,
          pages: loadedPages,
          slots: loadedSlots,
          linkUrl: loadedLinkUrl,
          altText: loadedAltText,
        });

        setInitialData({
          title: loadedTitle,
          mediaType: loadedMediaType,
          pages: [...loadedPages],
          slots: [...loadedSlots],
          linkUrl: loadedLinkUrl,
          altText: loadedAltText,
        });

        const url = ad.url || ad.mediaUrl || ad.media || "";
        if (url) {
          setOriginalMediaUrl(url);
          setExistingMediaUrl(url);
          setMediaPreview(url);
        }
        // Validate loaded URL immediately if invalid
        if (loadedLinkUrl.trim() && !isValidDestinationUrl(loadedLinkUrl.trim())) {
          setErrors((prev) => ({
            ...prev,
            linkUrl: "Please enter a valid URL (e.g., https://example.com)",
          }));
        }
      } catch (err) {
        console.error("Failed to load ad:", err);
        toast.error(getApiErrorMessage(err) || "Failed to load ad details");
        navigate("/manage-ads");
      } finally {
        setFetchLoading(false);
      }
    };

    loadAd();
  }, [id, isEditMode, navigate]);

  // Form change handler
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Reject switching to image if join_vedio slot is active
    if (name === "mediaType" && value !== "video" && formData.slots.includes("join_vedio")) {
      setErrors((prev) => ({ ...prev, mediaType: "Tournament Join Video slot requires Video media type" }));
      return;
    }

    if (name === "mediaType" && value !== formData.mediaType) {
      // User switched Media Type between image and video
      // If currently uploaded file or existing media conflicts with the new type, clear or handle gracefully
      if (mediaFile) {
        const fileMatches = value === "video" ? isVideoFileType(mediaFile) : isImageFileType(mediaFile);
        if (!fileMatches) {
          setMediaFile(null);
          setMediaPreview(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          toast.info(`Media Type changed to ${value === "video" ? "Video" : "Image"}. Please upload a ${value === "video" ? "video" : "image"} file.`);
        }
      } else if (existingMediaUrl) {
        const existingIsVid = checkIsVideo(existingMediaUrl, null, null);
        const urlMatches = value === "video" ? existingIsVid : !existingIsVid;
        if (!urlMatches) {
          setExistingMediaUrl("");
          setMediaPreview(null);
          toast.info(`Media Type changed to ${value === "video" ? "Video" : "Image"}. Please upload a ${value === "video" ? "video" : "image"} file.`);
        }
      } else if (isEditMode && originalMediaUrl) {
        const originalIsVid = checkIsVideo(originalMediaUrl, null, null);
        const matchesOriginal = value === "video" ? originalIsVid : !originalIsVid;
        if (matchesOriginal) {
          setExistingMediaUrl(originalMediaUrl);
          setMediaPreview(originalMediaUrl);
        }
      }
      // else if (existingMobileUrl) {
      //   const mobileUrlIsVid = checkIsVideo(existingMobileUrl, null, null);
      //   const mobileMatches = value === "video" ? mobileUrlIsVid : !mobileUrlIsVid;
      // } else if (isEditMode && originalMobileUrl) {
      //   const originalMobileIsVid = checkIsVideo(originalMobileUrl, null, null);
      //   const matchesMobileOriginal = value === "video" ? originalMobileIsVid : !originalMobileIsVid;
      // if (matchesMobileOriginal) {
      //   setExistingMobileUrl(originalMobileUrl);
      //   setMobileMediaPreview(originalMobileUrl);
      // }
      // }

      if (errors.media) {
        setErrors((prev) => ({ ...prev, media: "" }));
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "linkUrl") {
      const trimmed = value.trim();
      if (trimmed.length > 0 && !isValidDestinationUrl(trimmed)) {
        setErrors((prev) => ({
          ...prev,
          linkUrl: "Please enter a valid URL (e.g., https://example.com)",
        }));
      } else {
        setErrors((prev) => ({ ...prev, linkUrl: "" }));
      }
    } else if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    if (name === "linkUrl") {
      const trimmed = (value || "").trim();
      if (trimmed.length > 0 && !isValidDestinationUrl(trimmed)) {
        setErrors((prev) => ({
          ...prev,
          linkUrl: "Please enter a valid URL (e.g., https://example.com)",
        }));
      }
    }
  };

  // Pages multi-select
  const handlePagesChange = (event) => {
    const value = event.target.value;
    const selected = typeof value === "string" ? value.split(",") : value;

    // If 'all' was just clicked or is selected alone
    if (selected.includes("all") && !formData.pages.includes("all")) {
      setFormData((prev) => ({ ...prev, pages: ["all"] }));
    } else if (selected.includes("all") && selected.length > 1) {
      // User picked a specific page while 'all' was previously selected -> remove 'all'
      setFormData((prev) => ({ ...prev, pages: selected.filter((p) => p !== "all") }));
    } else {
      setFormData((prev) => ({ ...prev, pages: selected.length ? selected : ["all"] }));
    }

    if (errors.pages) {
      setErrors((prev) => ({ ...prev, pages: "" }));
    }
  };

  // Slots multi-select
  const handleSlotsChange = (event) => {
    const value = event.target.value;
    const selected = typeof value === "string" ? value.split(",") : value;

    const hasJoinVideo = selected.includes("join_vedio");
    const isMultiWithJoinVideo = hasJoinVideo && selected.length > 1;

    let updatedMediaType = formData.mediaType;
    if (hasJoinVideo) {
      updatedMediaType = "video";
      if (formData.mediaType !== "video") {
        if (mediaFile && !isVideoFileType(mediaFile)) {
          setMediaFile(null);
          setMediaPreview(null);
          if (fileInputRef.current) fileInputRef.current.value = "";
          toast.info("Tournament Join Video slot requires Video media type. Please upload a video file.");
        } else if (existingMediaUrl && !checkIsVideo(existingMediaUrl, null, null)) {
          setExistingMediaUrl("");
          setMediaPreview(null);
          toast.info("Tournament Join Video slot requires Video media type. Please upload a video file.");
        }
      }
    }

    setFormData((prev) => ({
      ...prev,
      slots: selected,
      mediaType: updatedMediaType,
    }));

    if (isMultiWithJoinVideo) {
      const errMsg = "Tournament Join Video can only be selected alone because it only accepts video format.";
      setErrors((prev) => ({ ...prev, slots: errMsg }));
    } else {
      if (errors.slots) {
        setErrors((prev) => ({ ...prev, slots: "" }));
      }
    }
  };

  // Primary media file handling & drag-drop
  const processPrimaryFile = (file) => {
    if (!file) return;

    // Strict validation: uploaded file must match selected mediaType
    if (formData.mediaType === "image") {
      if (!isImageFileType(file)) {
        const errMsg = "Selected Media Type is Image. Please upload an image file (JPG, PNG, GIF, WEBP).";
        toast.error(errMsg);
        setErrors((prev) => ({ ...prev, media: errMsg }));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    } else if (formData.mediaType === "video") {
      if (!isVideoFileType(file)) {
        const errMsg = "Selected Media Type is Video. Please upload a video file (MP4, WebM).";
        toast.error(errMsg);
        setErrors((prev) => ({ ...prev, media: errMsg }));
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
    }

    const sizeError = validateMediaFileSize(file, formData.mediaType);
    if (sizeError) {
      toast.error(sizeError);
      setErrors((prev) => ({ ...prev, media: sizeError }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (formData.slots.includes("join_vedio") && !isVideoFileType(file)) {
      const errMsg = "Tournament Join Video slot requires a video file (MP4, WebM)";
      toast.error(errMsg);
      setErrors((prev) => ({ ...prev, media: errMsg }));
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setMediaFile(file);
    const objectUrl = URL.createObjectURL(file);
    setMediaPreview(objectUrl);

    // Media type remains strictly what was selected in formData.mediaType - NO auto-switching!

    if (errors.media) {
      setErrors((prev) => ({ ...prev, media: "" }));
    }
  };

  const handleMediaFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processPrimaryFile(file);
    }
  };

  const handlePrimaryDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handlePrimaryDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handlePrimaryDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processPrimaryFile(file);
    }
  };

  const removeMedia = () => {
    setMediaFile(null);
    setMediaPreview(null);
    setExistingMediaUrl("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  // Change detection for Edit Mode
  // Title whitespace / empty space is not considered a change
  const isTitleChanged = normalizeSpaces(formData.title) !== normalizeSpaces(initialData?.title);
  const isAltTextChanged = normalizeSpaces(formData.altText) !== normalizeSpaces(initialData?.altText);
  const isLinkUrlChanged = (formData.linkUrl || "").trim() !== (initialData?.linkUrl || "").trim();
  const isMediaTypeChanged = formData.mediaType !== initialData?.mediaType;
  const isPagesChanged = !areArraysEqual(getPagesForPayload(formData.pages), getPagesForPayload(initialData?.pages));
  const isSlotsChanged = !areArraysEqual(formData.slots, initialData?.slots);
  const isMediaChanged = Boolean(mediaFile) || existingMediaUrl !== originalMediaUrl;
  const hasChanges = isEditMode
    ? Boolean(
      initialData &&
      (isTitleChanged ||
        isAltTextChanged ||
        isLinkUrlChanged ||
        isMediaTypeChanged ||
        isPagesChanged ||
        isSlotsChanged ||
        isMediaChanged
      )
    )
    : true;

  const trimmedTitle = formData.title.trim();
  const isTitleValid = trimmedTitle.length >= 3 && trimmedTitle.length <= 100;

  const trimmedAlt = (formData.altText || "").trim();
  const isAltTextValid = trimmedAlt.length === 0 || (trimmedAlt.length >= 3 && trimmedAlt.length <= 250);

  const isPagesValid = Boolean(formData.pages && formData.pages.length > 0);

  const isSlotsValid = Boolean(
    formData.slots &&
    formData.slots.length > 0 &&
    !(formData.slots.includes("join_vedio") && formData.slots.length > 1)
  );

  const isMediaTypeValid = !(formData.slots.includes("join_vedio") && formData.mediaType !== "video");

  const isMediaValid = Boolean(
    (mediaFile && checkIsMatchingType(mediaFile, null, formData.mediaType)) ||
    (existingMediaUrl && checkIsMatchingType(null, existingMediaUrl, formData.mediaType)) ||
    (!mediaFile && mediaPreview && checkIsMatchingType(null, mediaPreview, formData.mediaType))
  );

  const isLinkUrlValid = !formData.linkUrl.trim() || isValidDestinationUrl(formData.linkUrl.trim());

  const isFormValid =
    isTitleValid &&
    isAltTextValid &&
    isPagesValid &&
    isSlotsValid &&
    isMediaTypeValid &&
    isMediaValid &&
    isLinkUrlValid;

  // Validation
  const validateForm = () => {
    const newErrors = {};

    const currentTrimmedTitle = formData.title.trim();
    if (!currentTrimmedTitle) {
      newErrors.title = "Ad title is required";
    } else if (currentTrimmedTitle.length < 3) {
      newErrors.title = "Ad title must be at least 3 characters";
    } else if (currentTrimmedTitle.length > 100) {
      newErrors.title = "Ad title cannot exceed 100 characters";
    }

    const currentTrimmedAlt = formData.altText.trim();
    if (currentTrimmedAlt.length > 0) {
      if (currentTrimmedAlt.length < 3) {
        newErrors.altText = "Alt text must be at least 3 characters";
      } else if (currentTrimmedAlt.length > 250) {
        newErrors.altText = "Alt text cannot exceed 250 characters";
      }
    }

    if (!formData.pages || formData.pages.length === 0) {
      newErrors.pages = "Please select at least one page";
    }

    if (!formData.slots || formData.slots.length === 0) {
      newErrors.slots = "Please select at least one ad slot";
    } else if (formData.slots.includes("join_vedio") && formData.slots.length > 1) {
      newErrors.slots = "Tournament Join Video can only be selected alone because it only accepts video format.";
    }

    if (formData.slots.includes("join_vedio") && formData.mediaType !== "video") {
      newErrors.mediaType = "Tournament Join Video slot requires video media type";
    }

    if (!isEditMode && !mediaFile) {
      newErrors.media = `Please upload ${formData.mediaType === "video" ? "a video (MP4, WebM)" : "an image (JPG, PNG, GIF, WEBP)"}`;
    } else if (isEditMode && !mediaFile && !existingMediaUrl && !mediaPreview) {
      newErrors.media = `Please upload ${formData.mediaType === "video" ? "a video (MP4, WebM)" : "an image (JPG, PNG, GIF, WEBP)"}`;
    }

    if (mediaFile) {
      if (formData.mediaType === "video" && !isVideoFileType(mediaFile)) {
        newErrors.media = "Selected Media Type is Video. Please upload a video file (MP4, WebM).";
      } else if (formData.mediaType === "image" && !isImageFileType(mediaFile)) {
        newErrors.media = "Selected Media Type is Image. Please upload an image file (JPG, PNG, GIF, WEBP).";
      } else {
        const sizeErr = validateMediaFileSize(mediaFile, formData.mediaType);
        if (sizeErr) {
          newErrors.media = sizeErr;
        } else if (formData.slots.includes("join_vedio") && !isVideoFileType(mediaFile)) {
          newErrors.media = "Tournament Join Video slot requires a video file (MP4, WebM)";
        }
      }
    } else if (isEditMode && (existingMediaUrl || mediaPreview)) {
      const currentUrl = existingMediaUrl || mediaPreview;
      const urlIsVid = checkIsVideo(currentUrl, null, null);
      if (formData.mediaType === "video" && !urlIsVid) {
        newErrors.media = "Selected Media Type is Video. Please upload a video file.";
      } else if (formData.mediaType === "image" && urlIsVid) {
        newErrors.media = "Selected Media Type is Image. Please upload an image file.";
      }
    }

    // if (mobileMediaFile) {
    //   const mobileSizeErr = validateMediaFileSize(mobileMediaFile, formData.mediaType);
    //   if (mobileSizeErr) {
    //     newErrors.mobileMedia = mobileSizeErr;
    //   }
    // }

    const trimmedLinkUrl = formData.linkUrl.trim();
    if (trimmedLinkUrl.length > 0 && !isValidDestinationUrl(trimmedLinkUrl)) {
      newErrors.linkUrl = "Please enter a valid URL (e.g., https://example.com)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Form Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditMode && !hasChanges) {
      toast.info("No changes detected to update");
      return;
    }

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    try {
      setLoading(true);

      const submissionData = new FormData();

      if (isEditMode) {
        // Send ONLY fields that were actually updated/changed
        if (isTitleChanged) {
          submissionData.append("title", formData.title.trim());
        }
        if (isMediaTypeChanged) {
          submissionData.append("mediaType", formData.mediaType);
        }
        if (isPagesChanged) {
          const pagesToSend = getPagesForPayload(formData.pages);
          submissionData.append("pages", JSON.stringify(pagesToSend));
        }
        if (isSlotsChanged) {
          submissionData.append("slots", JSON.stringify(formData.slots));
        }
        if (isLinkUrlChanged) {
          submissionData.append("linkUrl", formData.linkUrl.trim());
        }
        if (isAltTextChanged) {
          submissionData.append("altText", formData.altText.trim());
        }
        if (mediaFile) {
          submissionData.append("media", mediaFile);
        }

        await updateAd(id, submissionData);
        toast.success("Ad updated successfully!");
      } else {
        // Create mode: send all fields, expanding 'all' to all specific page values
        submissionData.append("title", formData.title.trim());
        submissionData.append("mediaType", formData.mediaType);
        const pagesToSend = getPagesForPayload(formData.pages);
        submissionData.append("pages", JSON.stringify(pagesToSend));
        submissionData.append("slots", JSON.stringify(formData.slots));

        if (formData.linkUrl.trim()) {
          submissionData.append("linkUrl", formData.linkUrl.trim());
        }
        if (formData.altText.trim()) {
          submissionData.append("altText", formData.altText.trim());
        }

        if (mediaFile) {
          submissionData.append("media", mediaFile);
        }

        await createAd(submissionData);
        toast.success("Ad created successfully!");
      }

      navigate("/manage-ads");
    } catch (err) {
      console.error("Ad save error:", err);
      const msg = getApiErrorMessage(err) || "Failed to save ad. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={50} />
      </Box>
    );
  }

  const isPrimaryVideo = checkIsVideo(mediaPreview, mediaFile, formData.mediaType);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Top Header */}
      <Box display="grid" gridTemplateColumns="1fr auto 1fr" alignItems="center" mb={3} gap={2}>
        <Box>
          <Button
            variant="contained"
            onClick={() => navigate(isEditMode ? "/manage-ads" : "/Dashboard")}
            sx={{
              background: "linear-gradient(180deg, #c43dd6 0%, #9c27b0 100%)",
              textTransform: "uppercase",
              fontWeight: 600,
              px: 2.5,
              py: 1.2,
              borderRadius: "4px",
              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
              "&:hover": {
                background: "linear-gradient(180deg, #b036c0 0%, #8a229b 100%)",
              },
            }}
          >
            {isEditMode ? "Back to Manage Ads" : "Back to Dashboard"}
          </Button>
        </Box>
        <Typography variant="h4" fontWeight={700} color="text.primary" textAlign="center">
          {isEditMode ? "Edit Advertisement" : "Create New Advertisement"}
        </Typography>
        <Box />
      </Box>

      {/* Guide / Dimensions banner */}
      {/* <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 4,
          borderRadius: 3,
          background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
          color: "#fff",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
          <AspectRatioIcon sx={{ color: "#38bdf8" }} />
          <Typography variant="h6" fontWeight={700} sx={{ color: "#f8fafc" }}>
            Ad Slots & Dimensions Guide
          </Typography>
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#38bdf8">
                Top Banner
              </Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                Key: <b>top_banner</b>. Header banner displayed across selected pages.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#f472b6">
                Footer Banner
              </Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                Key: <b>footer_banner</b>. Banner placed in footer across selected pages.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#fbbf24">
                Tournament Join Video
              </Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                Key: <b>join_vedio</b>. Video shown when user clicks Join Tournament.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#34d399">
                Left Side Banner
              </Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                Key: <b>left_banner</b>. Sidebar ad banner on left side of views.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Box sx={{ p: 1.5, bgcolor: "rgba(255,255,255,0.06)", borderRadius: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} color="#a78bfa">
                Right Side Banner
              </Typography>
              <Typography variant="caption" sx={{ color: "#cbd5e1" }}>
                Key: <b>right_banner</b>. Sidebar ad banner on right side of views.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper> */}

      {/* Main Form */}
      <Card sx={{ borderRadius: 3, boxShadow: "0 8px 30px rgba(0,0,0,0.08)" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Ad Title *"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Global Chess Championship 2026 Partner"
                  error={Boolean(errors.title)}
                  helperText={errors.title || `${formData.title.length}/100 characters (min. 3)`}
                  inputProps={{ maxLength: 100 }}
                />
              </Grid>

              {/* Media Type */}
              <Grid item xs={12} md={4}>
                <FormControl fullWidth error={Boolean(errors.mediaType)}>
                  <InputLabel id="media-type-label">Media Type *</InputLabel>
                  <Select
                    labelId="media-type-label"
                    name="mediaType"
                    value={formData.mediaType}
                    label="Media Type *"
                    onChange={handleChange}
                  >
                    <MenuItem value="image" disabled={formData.slots.includes("join_vedio")}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <ImageIcon fontSize="small" color={formData.slots.includes("join_vedio") ? "disabled" : "primary"} /> Image (JPG, PNG, GIF, WEBP)
                      </Box>
                    </MenuItem>
                    <MenuItem value="video">
                      <Box display="flex" alignItems="center" gap={1}>
                        <MovieIcon fontSize="small" color="secondary" /> Video (MP4, WebM)
                      </Box>
                    </MenuItem>
                  </Select>
                  <FormHelperText error={Boolean(errors.mediaType)}>
                    {errors.mediaType ||
                      (formData.slots.includes("join_vedio")
                        ? "Locked to Video: Tournament Join Video slot requires video"
                        : "Select format type of media to upload")}
                  </FormHelperText>
                </FormControl>
              </Grid>

              {/* Target Pages */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(errors.pages)}>
                  <InputLabel id="pages-select-label">Target Pages in Chesswyze *</InputLabel>
                  <Select
                    labelId="pages-select-label"
                    multiple
                    value={formData.pages}
                    onChange={handlePagesChange}
                    input={<OutlinedInput label="Target Pages in Chesswyze *" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => {
                          const pageObj = AVAILABLE_PAGES.find((p) => p.value === val);
                          return (
                            <Chip
                              key={val}
                              label={pageObj ? pageObj.label : val}
                              size="small"
                              color={val === "all" ? "primary" : "default"}
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {AVAILABLE_PAGES.map((page) => (
                      <MenuItem key={page.value} value={page.value}>
                        <Checkbox checked={formData.pages.indexOf(page.value) > -1} />
                        <ListItemText primary={page.label} />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.pages ? (
                    <FormHelperText error>{errors.pages}</FormHelperText>
                  ) : (
                    <FormHelperText>
                      Choose which Chesswyze app pages will show this ad (or All Pages)
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Target Slots */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={Boolean(errors.slots)}>
                  <InputLabel id="slots-select-label">Target Ad Slots *</InputLabel>
                  <Select
                    labelId="slots-select-label"
                    multiple
                    value={formData.slots}
                    onChange={handleSlotsChange}
                    input={<OutlinedInput label="Target Ad Slots *" />}
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((val) => {
                          const slotObj = AVAILABLE_SLOTS.find((s) => s.value === val);
                          return (
                            <Chip
                              key={val}
                              label={slotObj ? slotObj.label : val}
                              size="small"
                              color="secondary"
                            />
                          );
                        })}
                      </Box>
                    )}
                  >
                    {AVAILABLE_SLOTS.map((slot) => (
                      <MenuItem key={slot.value} value={slot.value}>
                        <Checkbox checked={formData.slots.indexOf(slot.value) > -1} />
                        <ListItemText
                          primary={slot.label}
                          secondary={`${slot.badge} — ${slot.desc}`}
                        />
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.slots ? (
                    <FormHelperText error>{errors.slots}</FormHelperText>
                  ) : (
                    <FormHelperText>
                      Select positions where the ad will appear. Note: Tournament Join Video can only be selected alone.
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>

              {/* Destination Link URL */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Destination Link URL"
                  name="linkUrl"
                  value={formData.linkUrl}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="https://sponsor.com or https://partner.com/deal"
                  error={Boolean(errors.linkUrl)}
                  helperText={
                    errors.linkUrl || "User will be redirected to this link when clicking the ad (valid URL required if provided)"
                  }
                />
              </Grid>

              {/* Alt Text */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Alt Text / Accessibility Label"
                  name="altText"
                  value={formData.altText}
                  onChange={handleChange}
                  placeholder="e.g., Chess Partner Logo"
                  error={Boolean(errors.altText)}
                  helperText={errors.altText || `${formData.altText.length}/250 characters (optional, min. 3 if provided)`}
                  inputProps={{ maxLength: 250 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Primary Media Asset with Embedded In-Section Live Preview */}
              <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1.5}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                      Primary Media Asset {isEditMode ? "(Leave unchanged or choose new)" : "*"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Main creative banner or video asset displayed across selected ad slots
                    </Typography>
                  </Box>
                  {mediaPreview && (
                    <Chip
                      icon={
                        isPrimaryVideo ? (
                          <MovieIcon sx={{ fontSize: "16px !important" }} />
                        ) : (
                          <ImageIcon sx={{ fontSize: "16px !important" }} />
                        )
                      }
                      label={isPrimaryVideo ? "Video Creative" : "Image Creative"}
                      size="small"
                      color={isPrimaryVideo ? "secondary" : "primary"}
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>

                <input
                  type="file"
                  ref={fileInputRef}
                  hidden
                  accept={
                    formData.mediaType === "video"
                      ? "video/mp4,video/webm,video/ogg,video/quicktime,video/*"
                      : "image/jpeg,image/png,image/gif,image/webp,image/*"
                  }
                  onChange={handleMediaFileChange}
                />

                {!mediaPreview ? (
                  /* Empty Dropzone State */
                  <Paper
                    variant="outlined"
                    onDragOver={handlePrimaryDragOver}
                    onDragLeave={handlePrimaryDragLeave}
                    onDrop={handlePrimaryDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: "2px dashed",
                      borderColor: isDragging
                        ? "primary.main"
                        : errors.media
                          ? "error.main"
                          : "#cbd5e1",
                      borderRadius: 3,
                      p: { xs: 3, md: 5 },
                      textAlign: "center",
                      bgcolor: isDragging ? "primary.50" : "#f8fafc",
                      transition: "all 0.25s ease",
                      cursor: "pointer",
                      "&:hover": {
                        borderColor: "primary.main",
                        bgcolor: "#f0f7ff",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        bgcolor: formData.mediaType === "video" ? "#ede9fe" : "#e0f2fe",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                        color: formData.mediaType === "video" ? "#7c3aed" : "#0284c7",
                      }}
                    >
                      <CloudUploadIcon sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700} color="text.primary" gutterBottom>
                      {formData.mediaType === "video"
                        ? "Click or Drag & Drop Video File"
                        : "Click or Drag & Drop Image File"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ maxWidth: 520, mx: "auto", mb: 2 }}
                    >
                      {formData.mediaType === "video"
                        ? "Supports Videos only (MP4, WebM — max 200 MB)"
                        : "Supports Images only (JPG, PNG, GIF, WEBP — max 5 MB)"}
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        textTransform: "none",
                        borderRadius: 2,
                        fontWeight: 600,
                        px: 3,
                        bgcolor: formData.mediaType === "video" ? "#7c3aed" : "#0041A8",
                        "&:hover": {
                          bgcolor: formData.mediaType === "video" ? "#6d28d9" : "#003282",
                        },
                        pointerEvents: "none",
                      }}
                    >
                      {formData.mediaType === "video" ? "Choose Video File" : "Choose Image File"}
                    </Button>
                  </Paper>
                ) : (
                  /* Embedded In-Section Preview Card */
                  <Paper
                    elevation={0}
                    onDragOver={handlePrimaryDragOver}
                    onDragLeave={handlePrimaryDragLeave}
                    onDrop={handlePrimaryDrop}
                    sx={{
                      border: "1px solid",
                      borderColor: isDragging ? "primary.main" : "#e2e8f0",
                      borderRadius: 3,
                      overflow: "hidden",
                      bgcolor: "#ffffff",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                      position: "relative",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {/* Drag & Drop Overlay */}
                    {isDragging && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(2, 132, 199, 0.92)",
                          zIndex: 10,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          backdropFilter: "blur(2px)",
                        }}
                      >
                        <CloudUploadIcon sx={{ fontSize: 56, mb: 1 }} />
                        <Typography variant="h6" fontWeight={700}>
                          Drop new {formData.mediaType === "video" ? "video" : "image"} file here to replace
                        </Typography>
                      </Box>
                    )}

                    {/* Meta Bar & Action Controls */}
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1.5,
                        bgcolor: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1.5,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: isPrimaryVideo ? "#ede9fe" : "#e0f2fe",
                            color: isPrimaryVideo ? "#7c3aed" : "#0284c7",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {isPrimaryVideo ? <MovieIcon fontSize="small" /> : <ImageIcon fontSize="small" />}
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="text.primary"
                            noWrap
                            title={mediaFile ? mediaFile.name : existingMediaUrl}
                          >
                            {mediaFile
                              ? mediaFile.name
                              : (existingMediaUrl.split("/").pop() || "Active Ad Asset")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mediaFile
                              ? `${(mediaFile.size / (1024 * 1024)).toFixed(2)} MB • Newly selected`
                              : isEditMode
                                ? "Active saved ad media"
                                : "Ready to upload"}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Action Buttons */}
                      <Box display="flex" alignItems="center" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CloudUploadIcon />}
                          onClick={() => fileInputRef.current?.click()}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: "0.8125rem",
                          }}
                        >
                          Change {formData.mediaType === "video" ? "Video" : "Image"}
                        </Button>
                        <Tooltip title="Remove media">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={removeMedia}
                            sx={{
                              bgcolor: "#fee2e2",
                              "&:hover": { bgcolor: "#fecaca" },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Integrated Live Media Screen */}
                    <Box
                      sx={{
                        bgcolor: "#090d16",
                        p: { xs: 1.5, sm: 2.5 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 250,
                        maxHeight: 440,
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {isPrimaryVideo ? (
                        <video
                          src={mediaPreview}
                          controls
                          playsInline
                          style={{
                            maxWidth: "100%",
                            maxHeight: 400,
                            borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                            outline: "none",
                            objectFit: "contain",
                            backgroundColor: "#000",
                          }}
                        />
                      ) : (
                        <img
                          src={mediaPreview}
                          alt={formData.altText || "Ad preview"}
                          style={{
                            maxWidth: "100%",
                            maxHeight: 400,
                            borderRadius: 8,
                            boxShadow: "0 8px 24px rgba(0,0,0,0.5)",
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      )}
                    </Box>

                    {/* Footer strip */}
                    <Box
                      sx={{
                        px: 2.5,
                        py: 1,
                        bgcolor: "#f8fafc",
                        borderTop: "1px solid #f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        💡 You can click <b>Change Media</b> or drag-and-drop any new file here to replace.
                      </Typography>
                      <Typography variant="caption" color="primary.main" fontWeight={600}>
                        Live In-Section Preview
                      </Typography>
                    </Box>
                  </Paper>
                )}

                {errors.media && (
                  <Typography
                    variant="caption"
                    color="error.main"
                    sx={{ mt: 0.8, display: "block", fontWeight: 500 }}
                  >
                    {errors.media}
                  </Typography>
                )}

                {/* {originalMediaUrl && !mediaPreview && (
                  <Box mt={1.5} display="flex" alignItems="center" gap={1}>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={restoreOriginalMedia}
                      startIcon={<RefreshIcon fontSize="small" />}
                      sx={{ textTransform: "none", fontSize: "0.8125rem", borderRadius: 2 }}
                    >
                      Restore original saved ad media
                    </Button>
                  </Box>
                )} */}
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>

              {/* Optional Mobile Media (Responsive Override) */}
              {/* <Grid item xs={12}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1.5}
                  flexWrap="wrap"
                  gap={1}
                >
                  <Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <SmartphoneIcon fontSize="small" color="action" />
                      <Typography variant="subtitle1" fontWeight={700} color="text.primary">
                        Optional Mobile Media (Responsive Override)
                      </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Upload a mobile-optimized creative (optional). If not set, primary media will be used everywhere.
                    </Typography>
                  </Box>
                  {mobileMediaPreview && (
                    <Chip
                      icon={
                        isMobileVideo ? (
                          <MovieIcon sx={{ fontSize: "16px !important" }} />
                        ) : (
                          <ImageIcon sx={{ fontSize: "16px !important" }} />
                        )
                      }
                      label={isMobileVideo ? "Mobile Video" : "Mobile Image"}
                      size="small"
                      color="secondary"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>

                <input
                  type="file"
                  ref={mobileFileInputRef}
                  hidden
                  accept={
                    formData.mediaType === "video"
                      ? "video/mp4,video/webm,video/ogg,video/quicktime,video/*"
                      : "image/jpeg,image/png,image/gif,image/webp,image/*"
                  }
                  onChange={handleMobileFileChange}
                />

                {!mobileMediaPreview ? (
                  
                  <Paper
                    variant="outlined"
                    onDragOver={handleMobileDragOver}
                    onDragLeave={handleMobileDragLeave}
                    onDrop={handleMobileDrop}
                    onClick={() => mobileFileInputRef.current?.click()}
                    sx={{
                      border: "1px dashed",
                      borderColor: isMobileDragging ? "secondary.main" : "#cbd5e1",
                      borderRadius: 2.5,
                      p: 2.5,
                      textAlign: "center",
                      bgcolor: isMobileDragging ? "secondary.50" : "#f8fafc",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: "secondary.main",
                        bgcolor: "#faf5ff",
                      },
                    }}
                  >
                    <SmartphoneIcon sx={{ fontSize: 32, color: "secondary.main", mb: 0.5 }} />
                    <Typography variant="body2" fontWeight={600} color="text.primary">
                      Upload Mobile Specific File (Optional)
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Click or drag & drop a vertical / mobile-friendly creative
                    </Typography>
                  </Paper>
                ) : (
                  
                  <Paper
                    elevation={0}
                    onDragOver={handleMobileDragOver}
                    onDragLeave={handleMobileDragLeave}
                    onDrop={handleMobileDrop}
                    sx={{
                      border: "1px solid",
                      borderColor: isMobileDragging ? "secondary.main" : "#e2e8f0",
                      borderRadius: 3,
                      overflow: "hidden",
                      bgcolor: "#ffffff",
                      boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
                      position: "relative",
                    }}
                  >
                    {isMobileDragging && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          bgcolor: "rgba(124, 58, 237, 0.92)",
                          zIndex: 10,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#fff",
                          backdropFilter: "blur(2px)",
                        }}
                      >
                        <CloudUploadIcon sx={{ fontSize: 44, mb: 0.5 }} />
                        <Typography variant="body1" fontWeight={700}>
                          Drop new mobile media to replace
                        </Typography>
                      </Box>
                    )}

                    
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        bgcolor: "#f8fafc",
                        borderBottom: "1px solid #e2e8f0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1.25} sx={{ minWidth: 0, flex: 1 }}>
                        <Box
                          sx={{
                            width: 32,
                            height: 32,
                            borderRadius: 1.5,
                            bgcolor: "#f3e8ff",
                            color: "#7c3aed",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <SmartphoneIcon fontSize="small" />
                        </Box>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            variant="body2"
                            fontWeight={700}
                            color="text.primary"
                            noWrap
                            title={mobileMediaFile ? mobileMediaFile.name : existingMobileUrl}
                          >
                            {mobileMediaFile
                              ? mobileMediaFile.name
                              : (existingMobileUrl.split("/").pop() || "Mobile Media Asset")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mobileMediaFile
                              ? `${(mobileMediaFile.size / (1024 * 1024)).toFixed(2)} MB • Newly selected`
                              : "Active mobile override asset"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box display="flex" alignItems="center" gap={1}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => mobileFileInputRef.current?.click()}
                          sx={{
                            textTransform: "none",
                            borderRadius: 2,
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            py: 0.5,
                          }}
                        >
                          Change Mobile File
                        </Button>
                        <Tooltip title="Remove mobile override">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={removeMobileMedia}
                            sx={{ bgcolor: "#fee2e2", "&:hover": { bgcolor: "#fecaca" } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: "#090d16",
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 180,
                        maxHeight: 320,
                      }}
                    >
                      {isMobileVideo ? (
                        <video
                          src={mobileMediaPreview}
                          controls
                          playsInline
                          style={{
                            maxWidth: "100%",
                            maxHeight: 280,
                            borderRadius: 6,
                            outline: "none",
                            objectFit: "contain",
                            backgroundColor: "#000",
                          }}
                        />
                      ) : (
                        <img
                          src={mobileMediaPreview}
                          alt="Mobile ad preview"
                          style={{
                            maxWidth: "100%",
                            maxHeight: 280,
                            borderRadius: 6,
                            objectFit: "contain",
                            display: "block",
                          }}
                        />
                      )}
                    </Box>
                  </Paper>
                )}

                {originalMobileUrl && !mobileMediaPreview && (
                  <Box mt={1}>
                    <Button
                      size="small"
                      color="secondary"
                      onClick={restoreOriginalMobileMedia}
                      startIcon={<RefreshIcon fontSize="small" />}
                      sx={{ textTransform: "none", fontSize: "0.75rem" }}
                    >
                      Restore original mobile media
                    </Button>
                  </Box>
                )}
              </Grid> */}

              <Grid item xs={12} display="flex" justifyContent="flex-end" gap={2} mt={2}>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={() => navigate("/manage-ads")}
                  disabled={loading}
                  sx={{ borderRadius: 2, px: 4 }}
                >
                  Cancel
                </Button>
                <Tooltip
                  title={
                    !isFormValid
                      ? !isLinkUrlValid
                        ? "Please enter a valid Destination URL (e.g., https://example.com)"
                        : !isTitleValid
                          ? "Title must be between 3 and 100 characters"
                          : !isAltTextValid
                            ? "Alt text must be between 3 and 250 characters"
                            : !isMediaValid
                              ? `Please upload a valid ${formData.mediaType === "video" ? "video" : "image"} file`
                              : !isSlotsValid
                                ? "Tournament Join Video slot can only be selected alone"
                                : "Please fill all required fields correctly"
                      : isEditMode && !hasChanges
                        ? "No changes detected to update"
                        : ""
                  }
                  disableHoverListener={isFormValid && (!isEditMode || hasChanges)}
                  arrow
                  placement="top"
                >
                  <span>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={loading || !isFormValid || (isEditMode && !hasChanges)}
                      sx={{
                        borderRadius: 2,
                        px: 5,
                        bgcolor: "#0041A8",
                        "&:hover": { bgcolor: "#003282" },
                        "&.Mui-disabled": {
                          bgcolor: "rgba(0, 0, 0, 0.12)",
                          color: "rgba(0, 0, 0, 0.26)",
                        },
                      }}
                      startIcon={loading && <CircularProgress size={20} color="inherit" />}
                    >
                      {loading
                        ? isEditMode
                          ? "Updating..."
                          : "Creating..."
                        : isEditMode
                          ? "Update Ad"
                          : "Create Ad"}
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Container>
  );
};

export default CreateAd;
